import React, { useCallback, useState } from 'react';
import {
  Button,
  Col,
  Input,
  Row,
  Select,
  SelectOption,
} from '../../../components/ui';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { Modal as AntdModal } from 'antd';
import { isDomain } from '../../../services/forms';
import { useUnsubscribeDomain } from '../../../mutation/unsubscribe';
import { unsubscribeOrganizationEmailCount } from '../../../services/unsubscribe';
import { MAX_SENT_EMAIL_FOR_ORG_UNSUBSCRIPTION } from '../../../constants/common';
import { useGetAllEmailTypes } from '../../../query/mail';

const invalidDomains = [
  'gmail',
  'yahoo',
  'hotmail',
  'aol',
  'outlook',
  'hotmail',
  'msn',
  'wanadoo',
  'comcast',
  'live',
  'rediffmail',
];

const domainValidator = Joi.string().custom((value, helpers) => {
  if (invalidDomains.includes(value.split('.')[0])) {
    return helpers.message(
      `Invalid domain. Domain should not include ${invalidDomains.join(', ')}.`,
    );
  }
  const message = isDomain(value);
  if (message) {
    return helpers.message(message);
  }
  return value;
});

export default function DomainUnsubscribeForm() {
  const {
    data: emailTypesData,
    isLoading: emailTypesLoading,
  } = useGetAllEmailTypes();
  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues: { domain: '', emailTypeId: 0 },
    resolver: joiResolver(
      Joi.object().keys({
        domain: domainValidator,
        emailTypeId: Joi.number().optional(),
      }),
    ),
    mode: 'onBlur',
  });
  const { isLoading, mutateAsync } = useUnsubscribeDomain();
  const [isFetchingEmailsCount, setIsFetchingEmailsCount] = useState(false);
  const handleUnsubscribeDomain = useCallback(
    async ({ domain: currentDomain, emailTypeId }) => {
      try {
        setIsFetchingEmailsCount(true);
        const sentEmail = await unsubscribeOrganizationEmailCount({
          domain: currentDomain,
        });
        setIsFetchingEmailsCount(false);
        if (sentEmail[0].count <= MAX_SENT_EMAIL_FOR_ORG_UNSUBSCRIPTION) {
          /*
					Condition to show different messages based on email count
					More than 1 email from sent email is shown with email count. showing 1 email count doesn't
					form a proper sentence.
				*/
          const message =
            sentEmail[0].count > 1
              ? `There are ${sentEmail[0].count} emails from ${currentDomain}. 
            Are you sure you want to unsubscribe this domain and all these associated emails?`
              : `Are you sure you want to unsubscribe ${currentDomain} and all these associated emails?`;
          AntdModal.confirm({
            title: 'Unsubscribe Organization',
            content: message,
            onOk: () =>
              mutateAsync({ domain: currentDomain, emailTypeId }).then(() =>
                reset(),
              ),
          });
        } else {
          AntdModal.error({
            title: 'Unsubscribe Organization',
            content: `${sentEmail[0].count} emails match @${currentDomain}.
          We cannot unsubscribe all these emails at once. Please unsubscribe specific emails one by one.`,
          });
        }
      } catch (err) {
        AntdModal.error({
          title: 'Unsubscribe Organization',
          content: 'Request failed to reach the server',
        });
      }
    },
    [mutateAsync, reset],
  );
  return (
    <>
      <p style={{ marginBottom: 5, marginTop: 10 }}>
        Organization to unsubscribe
      </p>
      <Row gutter={8}>
        <Col span={12}>
          <Controller
            control={control}
            name="domain"
            render={({
              field: { value, onChange, onBlur },
              fieldState: { error },
            }) => {
              return (
                <Input
                  error={!!error && value}
                  helperTxt={!!value && error?.message}
                  id="organization"
                  name="organization"
                  placeholder="xyz.com"
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                />
              );
            }}
          />
        </Col>
        <Col span={5}>
          <Controller
            control={control}
            name="emailTypeId"
            render={({ field: { value, onChange } }) => {
              return (
                <Select value={value} onChange={onChange}>
                  <SelectOption key="default" value={0}>
                    All Email Types
                  </SelectOption>
                  {emailTypesData?.map(emailType => (
                    <SelectOption key={emailType.id} value={emailType.id}>
                      {emailType.type}
                    </SelectOption>
                  ))}
                </Select>
              );
            }}
          />
        </Col>
        <Col
          span={4}
          style={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          <Button
            disabled={
              !formState.isValid ||
              isLoading ||
              isFetchingEmailsCount ||
              emailTypesLoading
            }
            id="unsubscribe-organization"
            type="primary"
            onClick={handleSubmit(handleUnsubscribeDomain)}
          >
            unsubscribe
          </Button>
        </Col>
      </Row>
    </>
  );
}
